import { Injectable } from '@angular/core';

/**
 * Encrypts/decrypts sensitive localStorage values via Web Crypto (AES-GCM).
 * Keys are derived from the IndexedDB salt — not from viewport-dependent screen metrics.
 */
@Injectable({
  providedIn: 'root'
})
export class StorageEncryptionService {
  private readonly DB_NAME = 'ledger_encryption_db';
  private readonly DB_VERSION = 1;
  private readonly SALT_STORE = 'salt_store';
  private readonly SALT_KEY = 'encryption_salt';
  private readonly ALGORITHM = 'AES-GCM';
  private readonly KEY_LENGTH = 256;
  private readonly IV_LENGTH = 12;
  private readonly PBKDF2_ITERATIONS = 100000;
  /** Stable secret for salt-only key derivation (viewport-independent). */
  private readonly KEY_DERIVATION_SECRET = 'ledger-one-storage-key-v2';

  private encryptionKey: CryptoKey | null = null;
  private keyPromise: Promise<CryptoKey> | null = null;
  private db: IDBDatabase | null = null;

  constructor() {
    this.initializeKey();
  }

  private async initializeKey(): Promise<void> {
    try {
      this.encryptionKey = await this.getSaltOnlyKey();
    } catch (error) {
      console.error('Failed to initialize encryption key:', error);
      try {
        const salt = await this.getOrCreateSalt();
        this.encryptionKey = await this.deriveKey(this.KEY_DERIVATION_SECRET, salt);
      } catch (fallbackError) {
        console.error('Fallback key derivation failed:', fallbackError);
        this.encryptionKey = null;
      }
    }
  }

  private async getOrCreateSalt(): Promise<string> {
    try {
      const salt = await this.getSaltFromIndexedDB();
      if (salt) {
        return salt;
      }

      const newSalt = this.generateRandomSalt();
      await this.storeSaltInIndexedDB(newSalt);
      return newSalt;
    } catch (error) {
      console.warn('IndexedDB unavailable, using fallback salt:', error);
      return this.getFallbackSalt();
    }
  }

  private async getSaltFromIndexedDB(): Promise<string | null> {
    return new Promise((resolve, reject) => {
      if (!('indexedDB' in window)) {
        reject(new Error('IndexedDB not supported'));
        return;
      }

      const request = indexedDB.open(this.DB_NAME, this.DB_VERSION);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        const transaction = this.db.transaction([this.SALT_STORE], 'readonly');
        const store = transaction.objectStore(this.SALT_STORE);
        const getRequest = store.get(this.SALT_KEY);

        getRequest.onsuccess = () => {
          resolve(getRequest.result?.value || null);
        };

        getRequest.onerror = () => reject(getRequest.error);
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(this.SALT_STORE)) {
          db.createObjectStore(this.SALT_STORE);
        }
      };
    });
  }

  private async storeSaltInIndexedDB(salt: string): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.db) {
        const request = indexedDB.open(this.DB_NAME, this.DB_VERSION);
        request.onsuccess = () => {
          this.db = request.result;
          this.performSaltStore(salt, resolve, reject);
        };
        request.onerror = () => reject(request.error);
        request.onupgradeneeded = (event) => {
          const db = (event.target as IDBOpenDBRequest).result;
          if (!db.objectStoreNames.contains(this.SALT_STORE)) {
            db.createObjectStore(this.SALT_STORE);
          }
        };
      } else {
        this.performSaltStore(salt, resolve, reject);
      }
    });
  }

  private performSaltStore(salt: string, resolve: () => void, reject: (error: any) => void): void {
    if (!this.db) {
      reject(new Error('Database not initialized'));
      return;
    }

    const transaction = this.db.transaction([this.SALT_STORE], 'readwrite');
    const store = transaction.objectStore(this.SALT_STORE);
    const putRequest = store.put({ key: this.SALT_KEY, value: salt }, this.SALT_KEY);

    putRequest.onsuccess = () => resolve();
    putRequest.onerror = () => reject(putRequest.error);
  }

  private generateRandomSalt(): string {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  }

  private getFallbackSalt(): string {
    const fingerprint = this.getStableDeviceFingerprint();
    const encoder = new TextEncoder();
    const data = encoder.encode(fingerprint + 'ledger-one-salt-v1');
    return Array.from(data, byte => byte.toString(16).padStart(2, '0')).join('').substring(0, 64);
  }

  /** Viewport-independent fingerprint used for legacy fallback key derivation. */
  private getStableDeviceFingerprint(): string {
    const nav = navigator as any;
    const components = [
      navigator.userAgent,
      navigator.language,
      screen.colorDepth.toString(),
      new Date().getTimezoneOffset().toString(),
      navigator.hardwareConcurrency?.toString() || '',
      (nav.deviceMemory?.toString() || ''),
      navigator.platform
    ];

    return components.join('|');
  }

  /** Original fingerprint format (includes screen dimensions at encryption time). */
  private getLegacyDeviceFingerprint(screenDimensions: string): string {
    const nav = navigator as any;
    const components = [
      navigator.userAgent,
      navigator.language,
      screenDimensions,
      screen.colorDepth.toString(),
      new Date().getTimezoneOffset().toString(),
      navigator.hardwareConcurrency?.toString() || '',
      (nav.deviceMemory?.toString() || ''),
      navigator.platform
    ];

    return components.join('|');
  }

  private async deriveKey(keyMaterial: string, salt: string): Promise<CryptoKey> {
    const keyMaterialBuffer = new TextEncoder().encode(keyMaterial + salt);
    const importedKey = await crypto.subtle.importKey(
      'raw',
      keyMaterialBuffer,
      'PBKDF2',
      false,
      ['deriveBits', 'deriveKey']
    );

    const saltBuffer = new TextEncoder().encode(salt + 'ledger-one-pbkdf2-salt');

    return crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: saltBuffer,
        iterations: this.PBKDF2_ITERATIONS,
        hash: 'SHA-256'
      },
      importedKey,
      {
        name: this.ALGORITHM,
        length: this.KEY_LENGTH
      },
      false,
      ['encrypt', 'decrypt']
    );
  }

  private async getSaltOnlyKey(): Promise<CryptoKey> {
    const salt = await this.getOrCreateSalt();
    return this.deriveKey(this.KEY_DERIVATION_SECRET, salt);
  }

  private async getStableFingerprintKey(): Promise<CryptoKey> {
    const salt = await this.getOrCreateSalt();
    return this.deriveKey(this.getStableDeviceFingerprint(), salt);
  }

  private async getLegacyFingerprintKey(screenDimensions: string): Promise<CryptoKey> {
    const salt = await this.getOrCreateSalt();
    return this.deriveKey(this.getLegacyDeviceFingerprint(screenDimensions), salt);
  }

  /**
   * Build decrypt key candidates. Salt-only is tried first (current format).
   * Legacy keys use the screen dimensions from when the value was encrypted.
   */
  private async getDecryptKeyCandidates(screenHint?: string | null): Promise<CryptoKey[]> {
    const keys: CryptoKey[] = [
      await this.getSaltOnlyKey(),
      await this.getStableFingerprintKey(),
    ];

    const screenCandidates = new Set<string>();
    if (screenHint) {
      screenCandidates.add(screenHint);
    }
    screenCandidates.add(`${screen.width}x${screen.height}`);

    for (const dimensions of screenCandidates) {
      keys.push(await this.getLegacyFingerprintKey(dimensions));
    }

    return keys;
  }

  private async ensureKey(): Promise<CryptoKey> {
    if (this.encryptionKey) {
      return this.encryptionKey;
    }

    if (this.keyPromise) {
      return this.keyPromise;
    }

    this.keyPromise = (async () => {
      await this.initializeKey();
      if (!this.encryptionKey) {
        throw new Error('Failed to initialize encryption key');
      }
      this.keyPromise = null;
      return this.encryptionKey;
    })();

    return this.keyPromise;
  }

  async encrypt(data: string): Promise<string> {
    try {
      const key = await this.ensureKey();
      const iv = crypto.getRandomValues(new Uint8Array(this.IV_LENGTH));
      const encodedData = new TextEncoder().encode(data);
      const encrypted = await crypto.subtle.encrypt(
        { name: this.ALGORITHM, iv },
        key,
        encodedData
      );

      const combined = new Uint8Array(iv.length + encrypted.byteLength);
      combined.set(iv);
      combined.set(new Uint8Array(encrypted), iv.length);

      return btoa(String.fromCharCode(...combined));
    } catch (error) {
      console.error('Encryption failed, using obfuscation:', error);
      return this.simpleObfuscate(data);
    }
  }

  /**
   * @param screenHint Screen dimensions (WxH) used when the value was encrypted.
   *        Required to decrypt legacy tokens after a DevTools viewport change.
   */
  async decrypt(encryptedData: string, screenHint?: string | null): Promise<string> {
    if (!encryptedData || encryptedData.trim() === '') {
      throw new Error('Empty encrypted data');
    }

    const keys = await this.getDecryptKeyCandidates(screenHint);
    let lastError: unknown = null;

    for (const key of keys) {
      try {
        return await this.decryptWithKey(encryptedData, key);
      } catch (error) {
        lastError = error;
      }
    }

    const errorMessage = lastError instanceof Error ? lastError.message : String(lastError ?? '');
    const isLikelyObfuscated = encryptedData.length < 100 && !errorMessage.includes('Invalid encrypted data format');

    if (isLikelyObfuscated) {
      try {
        const deobfuscated = this.simpleDeobfuscate(encryptedData, screenHint);
        if (deobfuscated && deobfuscated.trim() !== '') {
          return deobfuscated;
        }
      } catch {
        // fall through
      }
    }

    throw new Error(`Decryption failed: ${errorMessage}`);
  }

  private async decryptWithKey(encryptedData: string, key: CryptoKey): Promise<string> {
    const combined = Uint8Array.from(atob(encryptedData), c => c.charCodeAt(0));

    if (combined.length < this.IV_LENGTH + 1) {
      throw new Error('Invalid encrypted data format: too short');
    }

    const iv = combined.slice(0, this.IV_LENGTH);
    const encrypted = combined.slice(this.IV_LENGTH);

    const decrypted = await crypto.subtle.decrypt(
      { name: this.ALGORITHM, iv },
      key,
      encrypted
    );

    const result = new TextDecoder().decode(decrypted);
    if (!result || result.trim() === '') {
      throw new Error('Decryption resulted in empty string');
    }

    return result;
  }

  private simpleObfuscate(data: string): string {
    const key = this.getStableDeviceFingerprint().substring(0, 16) + 'ledger-key';
    let result = '';
    for (let i = 0; i < data.length; i++) {
      result += String.fromCharCode(data.charCodeAt(i) ^ key.charCodeAt(i % key.length));
    }
    return btoa(result);
  }

  private simpleDeobfuscate(obfuscated: string, screenHint?: string | null): string {
    const fingerprints = [
      this.getStableDeviceFingerprint(),
      this.getLegacyDeviceFingerprint(screenHint || `${screen.width}x${screen.height}`),
    ];

    for (const fingerprint of fingerprints) {
      try {
        return this.deobfuscateWithFingerprint(obfuscated, fingerprint);
      } catch {
        // try next
      }
    }

    throw new Error('Deobfuscation failed');
  }

  private deobfuscateWithFingerprint(obfuscated: string, fingerprint: string): string {
    const data = atob(obfuscated);
    const key = fingerprint.substring(0, 16) + 'ledger-key';
    let result = '';
    for (let i = 0; i < data.length; i++) {
      result += String.fromCharCode(data.charCodeAt(i) ^ key.charCodeAt(i % key.length));
    }

    if (!result || result.trim() === '') {
      throw new Error('Deobfuscation resulted in empty string');
    }

    return result;
  }

  async clearEncryptionData(): Promise<void> {
    try {
      if (this.db) {
        const transaction = this.db.transaction([this.SALT_STORE], 'readwrite');
        const store = transaction.objectStore(this.SALT_STORE);
        await new Promise<void>((resolve, reject) => {
          const request = store.delete(this.SALT_KEY);
          request.onsuccess = () => resolve();
          request.onerror = () => reject(request.error);
        });
      }
      this.encryptionKey = null;
      this.keyPromise = null;
    } catch (error) {
      console.error('Failed to clear encryption data:', error);
    }
  }
}
